<template>
	<div class="widget widget-myday" :class="{ 'fullscreen-widget': isFullscreen }" ref="root">
		<v-card class="mx-auto" flat>
			<template v-if="!isLoading">
				<v-btn v-if="isSheetLayout" class="widget-back-fab" icon variant="flat" :aria-label="t('Back')" @click="toggleFullscreen">
					<v-icon>chevron_left</v-icon>
				</v-btn>

				<v-toolbar v-if="!isSheetLayout" color="transparent" density="compact" @click="toggleFullscreen">
					<div class="v-card-top-actions" v-if="isFullscreen">
						<span>
							<v-icon>chevron_left</v-icon>
							{{ t('Back') }}
						</span>
					</div>
					<v-toolbar-title>
						<v-icon>today</v-icon>
						{{ title }}
					</v-toolbar-title>
					<div class="v-card-top-actions">
						<v-icon v-if="!isFullscreen">arrow_circle_right</v-icon>
					</div>
				</v-toolbar>
				<v-card-text>
					<div :class="timelineClasses" ref="timelineContainerRef">
						<div class="myday-header" ref="timelineHeaderRef" @click="onTimelineHeaderClick" v-if="isTouchMode && isFullscreen">
							<div class="drag-bar"></div>
						</div>

						<div class="myday-date-nav-row">
							<WidgetMyDayDateNav v-model="selectedDate" :date-format="isTouchMode && isFullscreen ? 'EEEE d MMMM yyyy' : undefined" />

							<div class="myday-route-summary" v-if="isTouchMode && isFullscreen && activeRouteSummary">
								<span class="myday-route-summary__item">
									<v-icon>schedule</v-icon>
									{{ activeRouteSummary.duration }}
								</span>
								<span class="myday-route-summary__separator">&bull;</span>
								<span class="myday-route-summary__item">
									<v-icon>straighten</v-icon>
									{{ activeRouteSummary.distance }}
								</span>
							</div>
						</div>

						<div class="myday-appointments" v-if="!isTouchMode || !isFullscreen">
							<div>
								<div class="add-appointment" @click="goToPlanning">
									<span class="text-overflow">{{ t('AddAppointment') }}</span>
									<v-icon>calendar_add_on</v-icon>
								</div>
							</div>
							<div>
								<div class="visits-objective" @click="goToPlanning">
									<div class="background-fill" :style="{ height: ratio + '%' }"></div>
									<span class="text-overflow">{{ t('Visits') }}</span>
									<span class="count">{{ nbVisit }}/{{ nbVisitTotal }}</span>
								</div>
							</div>
							<div v-if="hasAssortOrder">
								<div class="sales" @click="goToOrdersTracking">
									<span class="text-overflow">{{ t('Sales') }}</span>
									<span class="count">{{ salesAmount }}</span>
								</div>
							</div>
						</div>

						<div class="appointment-route-infos" v-if="!isFullscreen">
							<div>
								<v-icon>store</v-icon>
								<span>{{ visitDuration }}</span>
							</div>
							<div>
								<v-icon>directions_car</v-icon>
								<span>{{ visitRouteDuration }}</span>
							</div>
							<div>
								<div class="total-duration">
									<v-icon>store</v-icon>
									<v-icon>add</v-icon>
									<v-icon>directions_car</v-icon>
								</div>
								<span :class="routeGapStatus?.class">
									<v-icon v-if="routeGapStatus?.warning">warning</v-icon>
									{{ visitRouteDurationTotal }}
								</span>
							</div>
							<div @click="drawRoutes" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<v-icon>conversion_path</v-icon>
								<span>{{ t('SeeAllRoutes') }}</span>
							</div>
						</div>

						<div class="appointment-route-infos" v-if="isFullscreen && isTouchMode">
							<FadeSlideGroup>
								<v-slide-group-item>
									<v-chip @click="goToNextAppointment" v-if="todayAppointments.length > 0 && !isAllValidated()" class="ma-1" :class="{ 'main-chip': !isRouteDrawn }">
										<v-icon>directions</v-icon>
										<span>{{ t('GoToNextAppointment') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip @click="drawRoutes" v-if="todayAppointments.length > 0 && !isAllValidated()" class="ma-1">
										<v-icon>conversion_path</v-icon>
										<span>{{ t('SeeAllRoutes') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip @click="openInApp()" v-if="todayAppointments.length > 0 && !isAllValidated()" class="ma-1" :class="{ 'route-start-chip': isRouteDrawn }" :color="isRouteDrawn ? 'primary' : undefined" :variant="isRouteDrawn ? 'flat' : undefined">
										<v-icon>navigation</v-icon>
										<span>{{ t('OpenInApp') }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<v-icon>store</v-icon>
										<span>{{ visitDuration }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<v-icon>directions_car</v-icon>
										<span>{{ visitRouteDuration }}</span>
									</v-chip>
								</v-slide-group-item>
								<v-slide-group-item>
									<v-chip class="ma-1">
										<div class="total-duration">
											<v-icon>store</v-icon>
											<v-icon>add</v-icon>
											<v-icon>directions_car</v-icon>
										</div>
										<span :class="routeGapStatus?.class">
											<v-icon v-if="routeGapStatus?.warning">warning</v-icon>
											{{ visitRouteDurationTotal }}
										</span>
									</v-chip>
								</v-slide-group-item>
							</FadeSlideGroup>
						</div>

						<v-timeline v-if="todayAppointments.length > 0" density="compact" class="deliveryLocations-timeline" truncate-line="both" :style="{ '--progress-percentage': progressPercentage + '%', '--completed-percentage': completedAppointmentsPercentage + '%', '--route-start': getRouteSegmentStart() + '%', '--route-end': getRouteSegmentEnd() + '%' }">
							<div class="timeline-progress-bar" ref="timelineProgressBar"></div>
							<template v-for="(todayAppointment, index) in todayAppointments" :key="index">
								<v-timeline-item v-if="isFullscreen && isNextStopRoute(todayAppointment)" dot-color="grey-lighten-1" size="x-small" class="route-info-item">
									<template #icon>
										<v-icon style="font-size: 24px; width: 40px; height: 40px; color: #1c79bb; display: flex; align-items: center; justify-content: center; background-color: #f0f0f0; border-radius: 50%; border: 2px solid #1c79bb">directions</v-icon>
									</template>
									<div class="route-info">
										<div class="route-details">
											<span class="route-distance">
												<v-icon>straighten</v-icon>
												{{ nextAppointmentRoute.distance }}
											</span>
											<span class="route-time">
												<v-icon>schedule</v-icon>
												{{ nextAppointmentRoute.duration }}
											</span>
										</div>
									</div>
								</v-timeline-item>

								<!-- Appointment item -->
								<v-timeline-item v-if="isFullscreen || (nextAppointmentAfterCompleted && index === nextAppointmentAfterCompleted.index)" :dot-color="todayAppointment.color" size="large" :class="todayAppointment.isHighlighted ? 'highlighted' : ''">
									<template #icon>
										<v-icon class="cursor-pointer" @click="seeAppointment(todayAppointment)">{{ todayAppointment.icon }}</v-icon>
									</template>
									<div class="deliveryLocation-item" :data-appointment-uid="todayAppointment.uid">
										<div class="deliveryLocation-item-line">
											<div class="delivery-location-sps-container" v-if="isFullscreen && todayAppointment.sps && !isMobile">
												<span class="delivery-location-sps">{{ todayAppointment.sps }}</span>
											</div>
											<div class="delivery-location-text">
												<span class="delivery-location-name text-secondary" @click="seeAppointment(todayAppointment)">
													{{ getDeliveryLocationChannel(todayAppointment) }}
													{{ !isPrivate(todayAppointment) ? todayAppointment.deliveryLocation?.label : t('PRIVATE') }}
												</span>
												<span class="delivery-location-info text-on-surface">
													<span v-if="todayAppointment.startDate">{{ new Date(todayAppointment.startDate).toLocaleDateString() }} {{ new Date(todayAppointment.startDate).toLocaleTimeString().substring(0, 5) }} - {{ new Date(todayAppointment.endDate).toLocaleTimeString().substring(0, 5) }} |</span>
													{{ todayAppointment.subTypeLabel }}
												</span>
											</div>
										</div>

										<div class="icons" v-if="!isFullscreen || (isFullscreen && isMobile)">
											<v-icon :class="todayAppointment.iconStatusClass">{{ todayAppointment.iconStatus }}</v-icon>
											<v-menu :model-value="openMenuId === todayAppointment.uid + index" @update:model-value="(val) => (openMenuId = val ? todayAppointment.uid + index : null)" v-bind="menuProps" teleport="false">
												<template v-slot:activator="{ props }">
													<span v-bind="props" :ref="'menuActivator-' + todayAppointment.uid + index">
														<v-icon>more_vert</v-icon>
													</span>
												</template>
												<v-list>
													<v-list-item v-if="iframeDataBridge.isOnline()" @click="handleMenuClick(todayAppointment, 'map')">
														<v-list-item-title>{{ t('SeeOnMap') }}</v-list-item-title>
													</v-list-item>
													<v-list-item v-if="!isUserHome(todayAppointment) && !isValidated(todayAppointment)" @click="handleMenuClick(todayAppointment, 'cancel')">
														<v-list-item-title>{{ t('Cancel') }}</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
										<div class="icons" v-else>
											<v-icon :class="todayAppointment.iconStatusClass">{{ todayAppointment.iconStatus }}</v-icon>
											<v-icon v-if="!isUserHome(todayAppointment) && !isValidated(todayAppointment)" @click="cancelAppointment(todayAppointment)" class="text-danger">cancel</v-icon>
											<v-icon v-if="iframeDataBridge.isOnline()" @click="showOnMap(todayAppointment)">location_on</v-icon>
										</div>
									</div>
								</v-timeline-item>
							</template>
						</v-timeline>

						<div v-if="todayAppointments.length === 0" class="no-appointments-message">
							<v-icon class="no-appointments-icon">event_busy</v-icon>
							<p>{{ t('NoAppointments') }}</p>
						</div>
					</div>

					<div :class="'right-container' + (timelineClasses['touch-mode'] ? ' touch-mode' : '')">
						<div class="appointment-route-infos" v-if="isFullscreen && !isTouchMode">
							<div>
								<v-icon>store</v-icon>
								<span>{{ visitDuration }}</span>
							</div>
							<div>
								<v-icon>directions_car</v-icon>
								<span>{{ visitRouteDuration }}</span>
							</div>
							<div>
								<div class="total-duration">
									<v-icon>store</v-icon>
									<v-icon>add</v-icon>
									<v-icon>directions_car</v-icon>
								</div>
								<span :class="routeGapStatus?.class">
									<v-icon v-if="routeGapStatus?.warning">warning</v-icon>
									{{ visitRouteDurationTotal }}
								</span>
							</div>
							<div @click="goToNextAppointment" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<v-icon>directions</v-icon>
								<span>{{ t('GoToNextAppointment') }}</span>
							</div>
							<div @click="drawRoutes" class="cursor-pointer" v-bind:disabled="todayAppointments.length === 0 || (isAllValidated() && todayAppointments.length > 0)">
								<v-icon>conversion_path</v-icon>
								<span>{{ t('SeeAllRoutes') }}</span>
							</div>
							<div v-if="hasRoute && todayAppointments.length > 0 && !isAllValidated()" @click="openInApp()" class="cursor-pointer" :class="{ 'route-start-action': isRouteDrawn }">
								<v-icon>navigation</v-icon>
								<span>{{ t('OpenInApp') }}</span>
							</div>
						</div>

						<MapLibre
							ref="mapRef"
							:locations="mapLocations"
							:all-locations="allLocations"
							:initial-zoom="10"
							:show-popup-on-click="true"
							:detail-marker-renderer="renderMarkerDetail"
							:tool-box="toolBox"
							:selected-date="selectedDate"
							location-id-key="id"
							location-title-key="label"
							:needFilter="true"
							@marker-click="onMarkerClick"
							@toolbox-action="onToolboxAction"
							@filters-changed="onFiltersChanged"
							@action-focus="handleActionFocus"
							@edit-delivery-location="(id) => handleEditDeliveryLocation(id, () => isFullscreen.value && toggleFullscreen())"
							@add-new-visit="handleAddNewVisit"
							@phone-number-copied="handlePhoneNumberCopied"
							:hide-detail-on-mini="true"
							:hide-route-summary="isSheetLayout"
							@route-summary-changed="onRouteSummaryChanged"
							@marker-detail-hidden="markerDetailHidden"
						/>
					</div>
				</v-card-text>
			</template>
			<template v-else>
				<v-skeleton-loader type="card, list-item, list-item-two-line, list-item-two-line" />
			</template>
		</v-card>

		<v-snackbar v-model="searchingPosition" :timeout="-1" :loading="true" rounded="xl" color="info">
			{{ t('SearchingUserPosition') }}
		</v-snackbar>

		<NavigationAppSheet v-model="navigationSheetOpen" :stops="hasRoute.stops" />
	</div>
</template>

<script setup>
import axios from 'axios';
import { ref, onMounted, onBeforeUnmount, computed, watch, inject, nextTick } from 'vue';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import MapLibre from '../map/MapLibre.vue';
import NavigationAppSheet from '../map/NavigationAppSheet.vue';
import WidgetMyDayDateNav from './WidgetMyDayDateNav.vue';
import FadeSlideGroup from '@/components/common/FadeSlideGroup.vue';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import { useTranslator } from '@/composables/useTranslator';
import { eventBus } from '@/utils/core/eventBus';
import { useVisibleRefresh } from '@/composables/useVisibleRefresh';
import { handleEditDeliveryLocation, handleAddNewVisit, handlePhoneNumberCopied, getDeliveryLocationTypeIcon, getDeliveryLocationTypeColor, getDeliveryLocationKPIs, getVisitFrequency, getLastVisitInfo, resetDetailMarkerData } from '@/utils/domain/appointmentUtils';
import { appointmentService } from '@/services/appointmentService';
import { getDirectOrders } from '@/api/sales/order';
import { ORDER_TYPE, filterOrders } from '@/services/directOrderService';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { overlayHandler } from '@/utils/ui/overlayHandler';
import { dragBottomSheetHandler } from '@/utils/ui/dragBottomSheetHandler';
import { getDayRouteStops, planDayRoute, getNextStop, geoPointLookup, isRoutableAppointment, positionToPoint, pointToPosition } from '@/utils/domain/dayRoute';
import { isSameDay } from 'date-fns';

const root = ref(null);
const t = useTranslator();
const sfaHelper = iframeDataBridge.getSfaHelper();
const salesHelper = iframeDataBridge.getSalesHelper();
const dateHelper = iframeDataBridge.getDateHelper();
const enumAppointment = iframeDataBridge.enumAppointment;
const user = iframeDataBridge.getCurrentUser();
const toast = iframeDataBridge.getIframeWindow().toast;
const iframeDocument = iframeDataBridge.getIframeWindow().document;

// #endregion

// #region STATE

const props = defineProps({
	title: String,
});

const isLoading = ref(true);
const isFullscreen = ref(false);
const isTouchMode = ref(deviceUtils.isTouchMode() && isFullscreen.value);
const selectedDate = ref(new Date());
const isSheetLayout = computed(() => isFullscreen.value && isTouchMode.value);
const activeRouteSummary = ref(null);
function onRouteSummaryChanged(summary) {
	activeRouteSummary.value = summary;
}

let visualViewportCleanup = null;
function syncVisualViewportOffset() {
	const vv = window.visualViewport;
	const target = iframeDocument?.documentElement;
	if (!vv || !target) return;
	const hidden = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
	target.style.setProperty('--myday-keyboard-inset', hidden + 'px');
}
function observeVisualViewport() {
	const vv = window.visualViewport;
	if (!vv || visualViewportCleanup) return;
	vv.addEventListener('resize', syncVisualViewportOffset);
	vv.addEventListener('scroll', syncVisualViewportOffset);
	visualViewportCleanup = () => {
		vv.removeEventListener('resize', syncVisualViewportOffset);
		vv.removeEventListener('scroll', syncVisualViewportOffset);
		iframeDocument?.documentElement?.style?.removeProperty('--myday-keyboard-inset');
		visualViewportCleanup = null;
	};
	syncVisualViewportOffset();
}

const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode() && isFullscreen.value;
	isMobile.value = deviceUtils.isMobile();
	resizeProgressBar();
};
const isMobile = ref(deviceUtils.isMobile());
const overlayTarget = inject('overlayTarget', null);
const menuProps = computed(() => ({
	attach: overlayTarget || undefined,
	scrim: true,
	scrollStrategy: 'close',
}));
const openMenuId = ref(null);
watch(openMenuId, () => {
	overlayHandler(openMenuId);
});
const navigationSheetOpen = ref(false);
const hasAssortOrder = ref(true);

const mapControl = inject('mapControl');
const mapRef = ref(null);
const showMap = ref(false);
const mapLocations = ref([]);
const allLocations = ref([]);
const deliveryLocationsGeo = ref([]);
const toolBox = ref(['allDeliveryLocations', 'offline']);
const searchingPosition = ref(false);
const hasRoute = ref({
	active: false,
	stops: [],
	sequence: [],
});
const isRouteDrawn = computed(() => hasRoute.value.active && Array.isArray(hasRoute.value.stops) && hasRoute.value.stops.length > 0);
let mapScriptLoadingPromise = null;

const todayAppointments = ref([]);
const nbVisit = ref(0);
const nbVisitTotal = ref(0);

const salesAmount = ref(0);
const visitDuration = ref(0);
const visitRouteDuration = ref(null);
const visitRouteDurationTotal = ref(null);
const routeGapStatus = ref(null);

const timelineProgressBar = ref(null);
const progressPercentage = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return 0;
	return calculateDayProgress();
});

const completedAppointmentsPercentage = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return 0;

	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	if (nonUserAppointments.length === 0) return 0;

	let lastCompletedIndex = -1;
	for (let i = 0; i < nonUserAppointments.length; i++) {
		if (nonUserAppointments[i].status === enumAppointment.status.VALIDATED) {
			lastCompletedIndex = i;
		}
	}

	const totalTimelineItems = todayAppointments.value.length;

	if (lastCompletedIndex === -1) return (1 / totalTimelineItems) * 100;
	let lastCompletedInTimeline = -1;
	for (let i = 0; i < todayAppointments.value.length; i++) {
		const appointment = todayAppointments.value[i];
		if (appointment.deliveryLocationId !== user.deliveryLocationHomeId && appointment.status === enumAppointment.status.VALIDATED && nonUserAppointments.indexOf(appointment) === lastCompletedIndex) {
			lastCompletedInTimeline = i;
			break;
		}
	}

	if (lastCompletedInTimeline === -1) return 1;

	return ((lastCompletedInTimeline + 0.5) / totalTimelineItems) * 100;
});

const nextAppointmentAfterCompleted = computed(() => {
	if (!todayAppointments.value || todayAppointments.value.length === 0) return null;

	let lastCompletedIndex = -1;
	for (let i = todayAppointments.value.length - 1; i >= 0; i--) {
		const apt = todayAppointments.value[i];
		if (apt.status === enumAppointment.status.VALIDATED && apt.deliveryLocationId !== user.deliveryLocationHomeId) {
			lastCompletedIndex = i;
			break;
		}
	}

	let nextIndex = lastCompletedIndex + 1;

	if (lastCompletedIndex === -1) {
		for (let i = 0; i < todayAppointments.value.length; i++) {
			const apt = todayAppointments.value[i];
			if (apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED) {
				nextIndex = i;
				break;
			}
		}
	}

	if (nextIndex >= todayAppointments.value.length) {
		return null;
	}

	for (let i = nextIndex; i < todayAppointments.value.length; i++) {
		const apt = todayAppointments.value[i];
		if (apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED) {
			return { appointment: apt, index: i };
		}
	}

	return null;
});

const nextAppointmentRoute = ref({
	distance: null,
	duration: null,
	nextAppointment: null,
});

const ratio = computed(() => {
	if (!nbVisitTotal.value || nbVisitTotal.value === 0) return 0;
	return Math.min((nbVisit.value / nbVisitTotal.value) * 100, 100).toFixed(2);
});

const SHEET_MID_RATIO = 0.4;
const SHEET_FULL_TOP_GAP = 24;
const dragSenderMode = ref('mid');
const timelineContainerRef = ref(null);
const timelineHeaderRef = ref(null);
const dragSenderIsDragging = ref(false);
const timelineContainerDrag = ref(null);

const timelineClasses = computed(() => {
	const cls = {
		'left-container': true,
		'touch-mode': isTouchMode.value,
	};
	if (isTouchMode.value) cls[dragSenderMode.value] = true;
	return cls;
});

// #endregion

// #region NAVIGATION APPS

async function openInApp() {
	if (!isRouteDrawn.value) {
		await drawRoutes();
	}

	if (!isRouteDrawn.value) {
		console.warn('No navigation links available');
		return;
	}
	navigationSheetOpen.value = true;
}

// #endregion

// #region LIFECYCLE

const init = async () => {
	if (iframeDataBridge.isOnline()) {
		initializeGoogleMaps();
	}
	mapRef.value?.clearHtmlMarkers();
	resetRoutes();
	resizeProgressBar();

	await getTodayAppointments();
	await getAllDeliveryLocations();
	await getOrders();
	isLoading.value = false;
};

useVisibleRefresh(['appointments-updated', 'agenda-updated'], init);

onMounted(() => {
	mapControl.drawMyDayRoutes = drawRoutes;
	iframeDataBridge.getStorage().get('DELIVERYLOCATIONGEO', (data) => {
		deliveryLocationsGeo.value = data || [];
	});
	window.addEventListener('resize', onResize);
	observeVisualViewport();
	init();
});

onBeforeUnmount(() => {
	if (timelineContainerDrag.value) timelineContainerDrag.value();
	window.removeEventListener('resize', onResize);
	if (visualViewportCleanup) visualViewportCleanup();
});

watch(selectedDate, init, { immediate: true });

function detachSheetDrag() {
	if (timelineContainerDrag.value) timelineContainerDrag.value();
	timelineContainerDrag.value = null;
}

function attachSheetDrag() {
	detachSheetDrag();

	const panelEl = timelineContainerRef.value;
	const handleEl = timelineHeaderRef.value;

	timelineContainerDrag.value = dragBottomSheetHandler(panelEl, handleEl, {
		isTouchMode,
		dragSenderMode,
		dragSenderIsDragging,
		iframeDocument,
		getSnaps: () => {
			const vh = window.innerHeight || iframeDocument.documentElement.clientHeight || 0;
			const topFull = SHEET_FULL_TOP_GAP;
			const topMid = Math.round(vh * (1 - SHEET_MID_RATIO));
			const topMini = Math.max(0, vh - 50 - 110);
			return { vh, topFull, topMid, topMini };
		},
	});
}

watch(
	() => isTouchMode.value,
	async (v) => {
		if (!isFullscreen.value) return;
		await nextTick();

		detachSheetDrag();
		if (!v) return;

		attachSheetDrag();
	},
	{ flush: 'post' }
);

watch(
	() => isFullscreen.value,
	async (v) => {
		isTouchMode.value = deviceUtils.isTouchMode() && v;
		if (!v) return;
		await nextTick();

		attachSheetDrag();
	}
);

watch(
	() => dragSenderMode.value,
	async () => {
		await nextTick();
		resizeProgressBar();

		if (dragSenderMode.value == 'mid') {
			scrollToNextAppointment();
		}
	}
);

// #endregion

// #region GOOGLE MAPS

function loadGoogleMaps() {
	if (!iframeDataBridge.isOnline()) return Promise.resolve();
	if (window.google?.maps) return Promise.resolve();
	if (mapScriptLoadingPromise) return mapScriptLoadingPromise;

	mapScriptLoadingPromise = new Promise((resolve) => {
		window.initMap = () => {
			resolve();
			window.initMap = undefined;
		};
		const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
		const script = document.createElement('script');
		script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&callback=initMap&loading=async&libraries=marker`;
		script.async = true;
		script.defer = true;
		document.head.appendChild(script);
	});

	return mapScriptLoadingPromise;
}

async function initializeGoogleMaps() {
	if (!iframeDataBridge.isOnline()) return;
	await loadGoogleMaps();
}

// #endregion

// #region APPOINTMENTS/DELIVERYLOCATIONS - DATA FETCHING

let previousNightAppointment = null;

function findPreviousNight(appointments, dayStart, dayEnd) {
	const nightSubTypeId = enumAppointment.getSubTypeId('NIGHT');
	return (appointments ?? []).filter((a) => a.subType === nightSubTypeId && a.status !== enumAppointment.status.CANCELLED && new Date(a.startDate) < dayStart && new Date(a.endDate) >= dayStart && new Date(a.endDate) < dayEnd).sort((a, b) => new Date(b.startDate) - new Date(a.startDate))[0] || null;
}

function getTodayAppointments() {
	return new Promise((resolve, reject) => {
		(async () => {
			try {
				const callback = getTodayAppointments;

				if (iframeDataBridge.isSyncEnded(['SFA_APPOINTMENTS', 'DELIVERYLOCATIONS'], callback)) {
					let startDate = new Date(selectedDate.value);
					startDate.setHours(0, 0, 0, 0);
					let endDate = new Date(startDate);
					endDate.setHours(23, 59, 59, 999);

					const success = async function (result) {
						try {
							const externalSubTypeId = enumAppointment.getSubTypeId('EXTERNAL');
							let appointments = (result ?? []).filter((appointment) => {
								return appointment.status !== enumAppointment.status.CANCELLED && dateHelper.isDateGreater(appointment.startDate, startDate, true) && dateHelper.isDateLess(appointment.startDate, endDate, true) && appointment.subType !== externalSubTypeId;
							});

							previousNightAppointment = findPreviousNight(result, startDate, endDate);
							todayAppointments.value = appointments.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
							getNbAppointments(todayAppointments.value);
							getVisitDurations(todayAppointments.value);
							getVisitRouteDuration(todayAppointments.value);
							getVisitRouteDurationTotal();
							await getAppointmentDeliveryLocations();

							getUserAppointment();

							resolve();
						} catch (err) {
							console.error('Error in getTodayAppointments success callback:', err);
							reject(err);
						}
					};

					const params = {
						startDate: startDate.toISOString(),
						endDate: endDate.toISOString(),
						userId: user.id,
					};
					await appointmentService.getAppointments(params, success);
				} else {
					resolve();
				}
			} catch (error) {
				console.error('Error in getTodayAppointments:', error);
				reject(error);
			}
		})();
	});
}

function getUserAppointment() {
	if (todayAppointments.value.length === 0) return;

	const ecosVM = iframeDataBridge.getEcosVM();
	let userHome = ecosVM?.deliveryLocations?.filter((deliveryLocation) => deliveryLocation.id === user.deliveryLocationHomeId);
	let isStartPositionNight = todayAppointments.value.length > 0 && todayAppointments.value[0].subType === sfaHelper.appointment.getDefaultSubtypeNight();
	let isEndPositionNight = todayAppointments.value.length > 0 && todayAppointments.value[todayAppointments.value.length - 1].subType === sfaHelper.appointment.getDefaultSubtypeNight();

	let userAppointment = {
		uid: 'home',
		label: t('UserHome'),
		startDate: null,
		endDate: null,
		deliveryLocationId: user.deliveryLocationHomeId,
		deliveryLocation: {
			id: userHome && userHome.length > 0 ? userHome[0].id : user.deliveryLocationHomeId,
			label: userHome && userHome.length > 0 ? userHome[0].label : '',
		},
		color: sfaHelper.getAppointmentColor({
			type: enumAppointment.type.CL,
			status: enumAppointment.status.VALIDATED,
		}),
		icon: 'home',
	};

	const hasStartUserHome = todayAppointments.value.map((ta) => ta.deliveryLocationId).indexOf(user.deliveryLocationHomeId) === 0;
	if (userHome && !isStartPositionNight && !hasStartUserHome) todayAppointments.value.unshift(getStartAppointment(userAppointment, ecosVM));

	const hasEndUserHome = todayAppointments.value.map((ta) => ta.deliveryLocationId).lastIndexOf(user.deliveryLocationHomeId) === todayAppointments.value.length - 1;
	if (userHome && !isEndPositionNight && !hasEndUserHome) todayAppointments.value.push(userAppointment);
}

function getStartAppointment(userAppointment, ecosVM) {
	if (!previousNightAppointment) return userAppointment;

	const nightDeliveryLocation = (ecosVM?.deliveryLocations || []).find((dl) => dl.id === previousNightAppointment.deliveryLocationId);
	if (!nightDeliveryLocation) return userAppointment;

	const nightSubType = (ecosVM?.appointmentSubtypes || []).find((subType) => subType.id === previousNightAppointment.subType);

	return {
		...userAppointment,
		uid: 'previous-night',
		label: nightDeliveryLocation.label,
		positionDeliveryLocationId: nightDeliveryLocation.id,
		deliveryLocation: nightDeliveryLocation,
		color: getDeliveryLocationTypeColor(nightDeliveryLocation, previousNightAppointment),
		icon: getDeliveryLocationTypeIcon(nightDeliveryLocation),
		subTypeLabel: nightSubType ? t(nightSubType.label) : '',
	};
}

function getAppointmentLocationId(todayAppointment) {
	return todayAppointment.positionDeliveryLocationId || todayAppointment.deliveryLocationId;
}

function getAllDeliveryLocations() {
	return new Promise((resolve) => {
		const callback = getAllDeliveryLocations;
		if (iframeDataBridge.isSyncEnded(['DELIVERYLOCATIONS'], callback)) {
			let success = function (result) {
				allLocations.value = result;
				resolve();
			};

			if (iframeDataBridge.isOnline()) {
				const url = iframeDataBridge.getService().sales.deliveryLocations.getUrl();
				const method = iframeDataBridge.getService().sales.deliveryLocations.type || 'get';
				const data = { userId: user.id };
				const headers = { Token: user.tokenSales };

				axios({
					method: method.toLowerCase(),
					url,
					headers,
					...(method.toUpperCase() === 'GET' ? { params: data } : { data }),
				})
					.then((response) => success(response.data))
					.catch((error) => {
						console.log(error.response || error);
						resolve();
					});
			} else {
				iframeDataBridge.getStorage().get('DELIVERYLOCATIONS', success);
			}
		}
	});
}

// #endregion

// #region APPOINTMENTS - DATA PROCESSING

async function getAppointmentDeliveryLocations() {
	const deliveryLocations = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONS');

	for (const todayAppointment of todayAppointments.value) {
		const deliveryLocation = deliveryLocations.find((dl) => dl.id === todayAppointment.deliveryLocationId);
		if (!deliveryLocation) continue;

		todayAppointment.color = getDeliveryLocationTypeColor(deliveryLocation, todayAppointment);
		let defaultTypeIcon = getDeliveryLocationTypeIcon(deliveryLocation);
		todayAppointment.icon = !todayAppointment.icon ? defaultTypeIcon : todayAppointment.icon;
		todayAppointment.deliveryLocation = deliveryLocation;

		let iconStatus = null;
		let iconStatusClass = null;

		if (sfaHelper.appointment.isInPrepa(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.PREPA;
		}

		if (sfaHelper.appointment.isInProgress(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.INPROGRESS;
		}

		if (await hasSyncError(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.SYNCERROR;
			iconStatusClass = 'text-danger';
		} else if (await hasPendingSync(todayAppointment)) {
			iconStatus = enumAppointment.iconStatus.PENDINGSYNC;
			iconStatusClass = 'text-warning';

			if (todayAppointment.status === enumAppointment.status.VALIDATED || todayAppointment._mustValidate) {
				iconStatusClass = 'text-warning';
			}
		}

		todayAppointment.iconStatus = iconStatus;
		todayAppointment.iconStatusClass = iconStatusClass;

		todayAppointment.sps = await getSPS(todayAppointment);

		const subTypes = iframeDataBridge.getEcosVM().appointmentSubtypes;
		const appointmentSubType = subTypes.find((subType) => subType.id === todayAppointment.subType);
		todayAppointment.subTypeLabel = appointmentSubType ? t(appointmentSubType.label) : '';
	}

	updateMapLocations();
}

function calculateDayProgress() {
	const nonUserAppointments = todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId && ta.status !== enumAppointment.status.CANCELLED);

	const completedAppointments = nonUserAppointments.filter((ta) => ta.status === enumAppointment.status.VALIDATED);

	if (nonUserAppointments.length === 0) {
		return 0;
	}

	return (completedAppointments.length / nonUserAppointments.length) * 100;
}

async function hasSyncError(appointment) {
	const offlineAppointments = await iframeDataBridge.getStorageAsync('OFFLINE-SFA_Appointments');

	if (!offlineAppointments) {
		return false;
	}

	return iframeDataBridge.getEcosVM()._OfflineErrors.some((obj) => obj.appointment.uid === appointment.uid) || (appointment.status !== enumAppointment.status.VALIDATED && offlineAppointments.some((o) => (o._localId === appointment.uid || o.uid === appointment.uid) && o.status === enumAppointment.status.VALIDATED)) || appointment._mustValidate;
}

async function hasPendingSync(appointment) {
	const offlineAppointments = await iframeDataBridge.getStorageAsync('OFFLINE-SFA_Appointments');

	if (!offlineAppointments) {
		return false;
	}

	return offlineAppointments.some((o) => o._localId === appointment.uid || o.uid === appointment.uid);
}

function keepMainAppointments(appointments) {
	return appointments.filter((appointment) => {
		return appointment.type == enumAppointment.type.CL;
	});
}

function getNbAppointments(appointments) {
	appointments = keepMainAppointments(appointments);

	nbVisitTotal.value = appointments.length;
	nbVisit.value = appointments.filter((appointment) => {
		return appointment.status === enumAppointment.status.VALIDATED;
	}).length;
}

function getVisitDurations(appointments) {
	appointments = keepMainAppointments(appointments);

	const nbMinutes = appointments.reduce((total, appointment) => {
		const minutes = dateHelper.getDiffMinutes(appointment.endDate, appointment.startDate);
		return total + Math.abs(minutes);
	}, 0);

	const time = dateHelper.convertMinutesToHoursMinutes(nbMinutes);
	visitDuration.value = `${time.h}h${String(time.m).padStart(2, '0')}`;
}

function getVisitRouteDuration(appointments) {
	let routing = sfaHelper.getRouting();

	let startDate = new Date(selectedDate.value);
	startDate.setHours(0, 0, 0, 0);
	let endDate = new Date(startDate);
	endDate.setHours(23, 59, 59, 999);

	let routingVisits = appointments.filter((appointment) => {
		return (appointment.type === enumAppointment.type.CL || appointment.subType === enumAppointment.getSubTypeId('NIGHT')) && ((new Date(appointment.startDate) >= startDate && new Date(appointment.startDate) < endDate) || (new Date(appointment.endDate) >= startDate && new Date(appointment.endDate) < endDate)) && appointment.status !== enumAppointment.status.CANCELLED;
	});

	if (routing.enabled) {
		let routeDuration = 0;

		for (var i = 0; i < routingVisits.length; i++) {
			let v = routingVisits[i];
			if (i === 0 && routing.enableFirstRoute && v.prevApp && !dateHelper.isDateLess(v.startDate, startDate)) {
				routeDuration += v.prevApp.routeDuration;
			}

			if (i >= 0 && i < routingVisits.length - 1) {
				if (v.nextApp) {
					routeDuration += v.nextApp.routeDuration;
				}
			}

			if (i === routingVisits.length - 1 && routing.enableLastRoute) {
				if (v.nextApp && !dateHelper.isDateGreater(v.endDate, endDate)) {
					routeDuration += v.nextApp.routeDuration;
				}
			}
		}
		if (routeDuration > 0) {
			const time = dateHelper.convertMinutesToHoursMinutes(routeDuration);
			visitRouteDuration.value = `${time.h}h${String(time.m).padStart(2, '0')}`;
		} else {
			visitRouteDuration.value = '0h00';
		}
	}
}

function getVisitRouteDurationTotal() {
	if (!visitDuration.value || !visitRouteDuration.value) {
		visitRouteDurationTotal.value = '0h00';
		return;
	}

	const parse = (str) => str.split('h').map(Number);
	const [h1 = 0, m1 = 0] = parse(visitDuration.value);
	const [h2 = 0, m2 = 0] = parse(visitRouteDuration.value);

	const totalMinutes = (h1 + h2) * 60 + m1 + m2;
	const finalHours = Math.floor(totalMinutes / 60);
	const finalMinutes = totalMinutes % 60;

	visitRouteDurationTotal.value = `${finalHours}h${String(finalMinutes).padStart(2, '0')}`;

	getRouteGap(visitRouteDurationTotal.value);
}

function getRouteGap(visitRouteDurationTotal) {
	const settings = salesHelper.getModule(sfaHelper.getApplicationDomainIdSFA(), 'Legislation').settings;
	const maxMinutes = (Number(settings.find((s) => s.name === 'MaxConsecutiveWorkDuration')?.value) || 999) * 60;

	const [h = 0, m = 0] = visitRouteDurationTotal.split('h').map(Number);
	const totalMinutes = h * 60 + m;

	if (totalMinutes === 0) {
		routeGapStatus.value = { class: '', warning: false };
		return;
	}

	const routeGap = maxMinutes - totalMinutes;
	let css = null;
	let warning = false;

	if (-30 <= routeGap && routeGap <= 30) {
		css = 'text-success';
	} else if (routeGap > 30) {
		css = 'text-warning';
	} else if (routeGap < -30) {
		css = 'text-danger';
		warning = true;
	}

	routeGapStatus.value = { class: css, warning };
}

async function getSPS(appointment) {
	try {
		const spsValue = await sfaHelper.kpi.templates.getSPSValue(appointment.deliveryLocationId);
		return spsValue ?? null;
	} catch (e) {
		return null;
	}
}

function getDeliveryLocationChannel(todayAppointment) {
	if (todayAppointment.deliveryLocation?.channelId) {
		const channels = iframeDataBridge.getEcosVM().channels;
		const channel = channels.find((c) => c.id === todayAppointment.deliveryLocation.channelId);
		return channel ? channel.name + ' - ' || '' : '';
	}

	return '';
}

function isUserHome(todayAppointment) {
	return todayAppointment.deliveryLocationId === user.deliveryLocationHomeId;
}

function isPrivate(todayAppointment) {
	return todayAppointment.subType === enumAppointment.getSubTypeId('PRIVATE');
}

function isValidated(todayAppointment) {
	return todayAppointment.status === enumAppointment.status.VALIDATED;
}

function isAllValidated() {
	return todayAppointments.value.filter((ta) => ta.deliveryLocationId !== user.deliveryLocationHomeId).every((ta) => ta.status === enumAppointment.status.VALIDATED);
}

// #endregion

// #region ACTIONS

function cancelAppointment(todayAppointment) {
	sfaHelper.cancelAppointment(todayAppointment, function () {
		amplify.publish('evtAgendaChange', { forceRefresh: true });
		eventBus.emit('appointments-updated');
	});
}

function showOnMap(todayAppointment) {
	if (!iframeDataBridge.isOnline()) {
		console.warn('Map is not available in offline mode');
		return;
	}
	if (!todayAppointment.deliveryLocationId) return;

	const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === getAppointmentLocationId(todayAppointment));

	if (!geo || !geo.latitude || !geo.longitude) {
		console.warn('No geo coordinates for this appointment');
		return;
	}

	updateMapLocations(true);
	showMap.value = true;

	if (isTouchMode.value && timelineContainerDrag.value && dragSenderMode.value !== 'mini') {
		timelineContainerDrag.value.setMode('mini');
	}

	if (!isFullscreen.value) {
		isFullscreen.value = true;
		eventBus.emit('widget-fullscreened', {
			widget: 'widget-myday',
			fullScreened: true,
			callback: async () => {
				resizeMap();
				await nextTick();
				if (mapRef.value && mapRef.value.setCenterZoom) {
					mapRef.value.setCenterZoom([geo.longitude, geo.latitude], 15);
				}
			},
		});
	} else {
		nextTick(() => {
			if (mapRef.value && mapRef.value.setCenterZoom) {
				mapRef.value.setCenterZoom([geo.longitude, geo.latitude], 15);
			}
		});
	}
}

async function goToNextAppointment() {
	resetDrawnRoute();

	if (!iframeDataBridge.isOnline()) {
		console.warn('Cannot calculate route: offline mode');
		return;
	}

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}

	getNextAppointmentRouteInfo();

	const nextStop = getNextStop(getDayRoute());

	if (nextStop && mapRef.value) {
		let waypoints = null;
		searchingPosition.value = true;
		try {
			waypoints = await mapRef.value.navigateToLocation(nextStop.point[1], nextStop.point[0], nextStop.appointment.deliveryLocation?.label || 'Prochain RDV', pointToPosition(nextStop.fallbackOrigin));
		} finally {
			searchingPosition.value = false;
		}

		if (waypoints) {
			hasRoute.value = { active: true, stops: [nextStop.point], sequence: waypoints };
		}
	} else {
		console.warn('No next appointment with geo coordinates');
	}

	dragSenderMode.value = 'mini';
}

async function zoomToNextAppointment() {
	const nextAppointment = nextAppointmentAfterCompleted.value;

	if (nextAppointment && mapRef.value) {
		const nextGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === nextAppointment.appointment.deliveryLocationId);

		if (nextGeo) {
			mapRef.value.setCenterZoom([nextGeo.longitude, nextGeo.latitude], 15);
		}
	}
}

async function scrollToNextAppointment() {
	const nextAppointment = nextAppointmentAfterCompleted.value;

	if (nextAppointment) {
		const timelineEl = timelineContainerRef.value.querySelector('.v-timeline');
		const appointmentEl = timelineEl.querySelector(`[data-appointment-uid="${nextAppointment.appointment.uid}"]`);

		if (appointmentEl) {
			setTimeout(() => {
				const timelineRect = timelineEl.getBoundingClientRect();
				const appointmentRect = appointmentEl.getBoundingClientRect();

				if (appointmentRect.top < timelineRect.top || appointmentRect.bottom > timelineRect.bottom) {
					const offset = appointmentRect.top - timelineRect.top - timelineRect.height / 2 + appointmentRect.height / 2;
					timelineEl.scrollBy({ top: offset, behavior: 'smooth' });
				}
			}, 300);
		}
	}
}

async function drawRoutes() {
	resetDrawnRoute();

	if (!iframeDataBridge.isOnline()) {
		console.warn('Route calculation is not available in offline mode');
		toast('Route calculation is not available in offline mode', { type: 'warning' });
		return;
	}

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}

	const dayRoute = getDayRoute();
	const origin = isSameDay(selectedDate.value, new Date()) ? positionToPoint(await getLivePosition()) : null;
	const plan = planDayRoute({ ...dayRoute, origin });

	if (plan.waypoints.length < 2) {
		console.warn('No appointments to display on the map');
		return;
	}

	showMap.value = true;
	await mapRef.value?.drawRoutePlan(plan);
	hasRoute.value = { active: true, stops: plan.navigation, sequence: plan.sequence };

	getNextAppointmentRouteInfo();
	updateMapLocations();

	if (!isFullscreen.value) {
		isFullscreen.value = true;
		nextTick(() => {
			eventBus.emit('widget-fullscreened', {
				widget: 'widget-myday',
				fullScreened: true,
				callback: async () => {
					resizeMap();
					setTimeout(() => {
						if (mapRef.value) {
							mapRef.value.fitAll();
						}
					}, 1);
				},
			});
		});
	}

	await nextTick();
	mapRef.value.ensureSourcesAndLayers();
	dragSenderMode.value = 'mini';
}

function goToPlanning() {
	amplify.publish('evtChangeView', { appMenu: 'menuSFA', view: 'vwSFAPlanning', data: { gotoDate: selectedDate.value.toISOString() } });
}

function goToOrdersTracking() {
	amplify.publish('evtChangeView', { appMenu: 'menuSALES', label: 'Order tracking Assort', view: 'vwOrdersTrackingAssort' });
}

function seeAppointment(todayAppointment) {
	if (!isUserHome(todayAppointment)) {
		const iframeWindow = iframeDataBridge.getIframeWindow();
		if (typeof iframeWindow?.externis?.sales?.vmAgenda == 'undefined') {
			iframeWindow.externis.sales.vmAgenda = {};
		}

		if (iframeWindow?.externis?.sales?.vmAgenda && iframeWindow.externis.sales.vmAgenda.datasource == null) {
			iframeWindow.externis.sales.vmAgenda.datasource = todayAppointments.value.map((a) => ({
				...a,
				startDate: a.startDate instanceof Date ? a.startDate : new Date(a.startDate),
				endDate: a.endDate instanceof Date ? a.endDate : new Date(a.endDate),
			}));
		}
		amplify.publish('evtAppointment', { appointment: todayAppointment });
	}
}

function onTimelineHeaderClick() {
	if (!isTouchMode.value) return;
	if (dragSenderIsDragging.value) return;
	if (typeof timelineContainerDrag.value?.setMode === 'function') {
		timelineContainerDrag.value.setMode(dragSenderMode.value == 'full' ? 'mini' : dragSenderMode.value == 'mini' ? 'mid' : 'full');
	}
}

function onMarkerClick(props) {
	if (isTouchMode.value && timelineContainerDrag.value && dragSenderMode.value !== 'mini') {
		timelineContainerDrag.value.setMode('mini');
	}
}

function markerDetailHidden() {
	highlightAppointment();
}

// #endregion

// #region ROUTE CALCULATION

function resetDrawnRoute() {
	hasRoute.value = { active: false, stops: [], sequence: [] };
}

function getDayRoute() {
	const dayStart = new Date(selectedDate.value);
	dayStart.setHours(0, 0, 0, 0);

	return getDayRouteStops({
		appointments: [previousNightAppointment, ...todayAppointments.value].filter(Boolean),
		dayStart,
		getPoint: geoPointLookup(deliveryLocationsGeo.value),
		homeId: user.deliveryLocationHomeId,
		routing: sfaHelper.getRouting(),
		enumAppointment,
	});
}

async function getLivePosition() {
	searchingPosition.value = true;
	try {
		return (await mapRef.value?.getLivePosition()) || null;
	} finally {
		searchingPosition.value = false;
	}
}

function resetRoutes() {
	resetDrawnRoute();

	nextAppointmentRoute.value = {
		distance: null,
		duration: null,
		nextAppointment: null,
	};

	if (mapRef.value) {
		mapRef.value.clearRoutes();
	}
}

async function getNextAppointmentRouteInfo() {
	try {
		nextAppointmentRoute.value = {
			distance: null,
			duration: null,
			nextAppointment: null,
		};

		const nextStop = getNextStop(getDayRoute());
		if (!nextStop || !mapRef.value) return;

		const position = isSameDay(selectedDate.value, new Date()) && mapRef.value.isGeolocationActive() ? positionToPoint(mapRef.value.getUserPosition()) : null;
		const origin = position || nextStop.fallbackOrigin;
		if (!origin) return;

		const routeInfo = await mapRef.value.getRouteSummary([origin, nextStop.point]);
		if (routeInfo?.distance && routeInfo?.duration) {
			nextAppointmentRoute.value = { distance: routeInfo.distance, duration: routeInfo.duration, nextAppointment: nextStop.appointment };
		}
	} catch (error) {
		console.error('Error calculating next appointment route info:', error);
	}
}

function isNextStopRoute(todayAppointment) {
	const nextAppointment = nextAppointmentRoute.value.nextAppointment;
	return !!nextAppointment?.uid && nextAppointment.uid === todayAppointment.uid;
}

function updateMapLocations(skipFitAll = false) {
	const needAdditionalLocations = mapRef.value?.getToolState('allDeliveryLocations')?.active;
	const additionnalLocations = needAdditionalLocations ? allLocations.value : null;
	const appointmentsToRoute = todayAppointments.value
		.filter((ta) => ta.deliveryLocation && ta.status != enumAppointment.status.CANCELLED && (isUserHome(ta) || isRoutableAppointment(ta, enumAppointment)))
		.map((ta) => {
			const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === getAppointmentLocationId(ta));

			return {
				...ta,
				geo: geo,
				hasValidGeo: !!(geo && geo.latitude && geo.longitude),
			};
		})
		.filter((ta) => ta.hasValidGeo);

	const sequenceMap = new Map();
	const pushSequence = (locationId, position) => {
		if (!locationId) return;
		if (!sequenceMap.has(locationId)) {
			sequenceMap.set(locationId, []);
		}
		sequenceMap.get(locationId).push(position);
	};

	const approxDistanceMeters = (lat1, lon1, lat2, lon2) => {
		const R = 6371000;
		const toRad = (d) => (d * Math.PI) / 180;
		const dLat = toRad(lat2 - lat1);
		const dLon = toRad(lon2 - lon1);
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c;
	};

	const getSequenceLabel = (locationId) => {
		if (!sequenceMap.has(locationId)) return null;

		const positions = sequenceMap
			.get(locationId)
			.filter((p) => Number.isFinite(p))
			.sort((a, b) => a - b);

		if (positions.length === 0) return null;

		const first = positions[0];
		const last = positions[positions.length - 1];

		return first === last ? first.toString() : `${first}-${last}`;
	};

	if (isRouteDrawn.value) {
		const locIndex = appointmentsToRoute.map((ta) => ({
			locationId: getAppointmentLocationId(ta),
			lat: ta.geo.latitude,
			lng: ta.geo.longitude,
		}));

		const maxMatchDistanceMeters = 150;

		hasRoute.value.sequence.forEach((wp, index) => {
			const wpLat = wp[1];
			const wpLng = wp[0];

			let best = null;

			for (let i = 0; i < locIndex.length; i++) {
				const loc = locIndex[i];
				const d = approxDistanceMeters(wpLat, wpLng, loc.lat, loc.lng);

				if (!best || d < best.d) {
					best = { d, locationId: loc.locationId };
				}
			}

			if (best && best.d <= maxMatchDistanceMeters) {
				pushSequence(best.locationId, index + 1);
			}
		});
	} else {
		appointmentsToRoute.forEach((ta, index) => {
			pushSequence(getAppointmentLocationId(ta), index + 1);
		});
	}

	const markerAppointments = appointmentsToRoute.filter((ta) => !ta.positionDeliveryLocationId || !appointmentsToRoute.some((other) => !other.positionDeliveryLocationId && other.deliveryLocationId === ta.positionDeliveryLocationId));

	const finalLocations = markerAppointments.map((ta) => {
		const sequence = getSequenceLabel(getAppointmentLocationId(ta));
		return {
			id: getAppointmentLocationId(ta),
			label: ta.deliveryLocation.label,
			deliveryLocationTypeId: ta.deliveryLocation.deliveryLocationTypeId,
			_geo: {
				latitude: ta.geo.latitude,
				longitude: ta.geo.longitude,
			},
			accountId: ta.deliveryLocation.accountId,
			icon: ta.icon || 'storefront',
			color: ta.color,
			appointmentData: ta,
			sequence: sequence,
		};
	});

	// Additionnal locations from tool AllDeliveryLocations
	if (needAdditionalLocations) {
		additionnalLocations.forEach((loc) => {
			const exists = finalLocations.find((ml) => ml.id === loc.id);
			if (!exists) {
				const geo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === loc.id);
				if (geo && geo.latitude && geo.longitude) {
					loc.lat = geo.latitude;
					loc.lng = geo.longitude;
				}

				finalLocations.push(loc);
			}
		});
	}

	mapLocations.value = finalLocations;

	if (!skipFitAll) {
		nextTick(() => {
			if (mapRef.value) {
				mapRef.value.fitAll();
			}
		});
	}

	nextTick(() => {
		if (mapRef.value) {
			setTimeout(() => {
				mapRef.value?.applyFilters();
			}, 300);
		}
	});
}

async function renderMarkerDetail(markerData, iframeDocument, detailMarkerData) {
	resetDetailMarkerData(detailMarkerData, markerData);

	let appointmentData = typeof markerData.appointmentData == 'string' ? JSON.parse(markerData.appointmentData) : markerData.appointmentData || null;
	let deliveryLocation = null;
	const isAdditionalMarker = !appointmentData;

	// Additional marker (deliveryLocation only)
	if (isAdditionalMarker) {
		deliveryLocation = markerData;
		// get appointment data for this delivery location if exists
		await appointmentService
			.getAppointments({ deliveryLocationId: deliveryLocation.id })
			.then((appointments) => {
				if (appointments && appointments.length > 0) {
					appointmentData = appointments[0];
				}
			})
			.catch(() => {
				appointmentData = null;
			});
	}
	// Classic appointment marker
	else {
		deliveryLocation = appointmentData.deliveryLocation || null;
	}

	const ecosVM = iframeDataBridge.getEcosVM();
	const deliveryLocationId = deliveryLocation?.id || appointmentData?.deliveryLocationId || null;
	detailMarkerData.value.title = markerData.label || '';
	detailMarkerData.value.id = markerData.id || null;
	detailMarkerData.value.phoneNumber = deliveryLocation ? deliveryLocation.phoneNumber || null : null;
	detailMarkerData.value.lat = markerData.lat || markerData.latitude || appointmentData.lat || deliveryLocation?.lat || null;
	detailMarkerData.value.lng = markerData.lng || markerData.longitude || appointmentData.lng || deliveryLocation?.lng || null;

	const addressParts = [];
	if (deliveryLocation) {
		if (deliveryLocation.addressLine) addressParts.push(deliveryLocation.addressLine);
		if (deliveryLocation.addressLine2) addressParts.push(deliveryLocation.addressLine2);
		if (deliveryLocation.city) addressParts.push(deliveryLocation.city);
		if (deliveryLocation.postcode) addressParts.push(deliveryLocation.postcode);
		if (deliveryLocation.country) addressParts.push(deliveryLocation.country);
	}

	detailMarkerData.value.address = addressParts.join(', ');
	detailMarkerData.value.lastVisitComment = '';

	if (deliveryLocationId) {
		try {
			const lastVisit = appointmentData ? await getLastVisitInfo(deliveryLocationId, appointmentData.startDate || null) : null;
			const lastComment = lastVisit && lastVisit.comment && lastVisit.comment.trim() ? lastVisit.comment : '';
			detailMarkerData.value.lastVisitComment = lastComment;
		} catch (e) {
			detailMarkerData.value.lastVisitComment = '';
		}
	}

	if (appointmentData && appointmentData.sps !== undefined && appointmentData.sps !== null) {
		detailMarkerData.value.sps = appointmentData.sps;
	} else {
		detailMarkerData.value.sps = null;
	}

	detailMarkerData.value.accountLogo = null;
	detailMarkerData.value.accountName = '';

	if (markerData.accountId) {
		const account = ecosVM.accounts && typeof ecosVM.accounts === 'function' ? ecosVM.accounts().find((a) => a.id === markerData.accountId) : (ecosVM.accounts || []).find((a) => a.id === markerData.accountId);

		if (account) {
			detailMarkerData.value.accountName = account.Name || account.name || '';

			const imageUId = account.imageUId || account.ImageUId || null;
			if (imageUId && imageUId !== '00000000-0000-0000-0000-000000000000') {
				try {
					if (iframeDataBridge.isOnline()) {
						const { getAccountImageUrl } = await import('@/api/core/file');
						const imageUrl = await getAccountImageUrl(imageUId);
						detailMarkerData.value.accountLogo = imageUrl || null;
					}
				} catch (e) {
					detailMarkerData.value.accountLogo = null;
				}
			}
		}
	}

	const fields = {};

	const deliveryLocationTypes = ecosVM.deliveryLocationTypes || [];
	const deliveryLocationTypeId = deliveryLocation ? deliveryLocation.deliveryLocationTypeId : markerData.deliveryLocationTypeId || null;
	const deliveryLocationType = deliveryLocationTypes.find((type) => type.id === deliveryLocationTypeId);
	if (deliveryLocationType) {
		fields[t('Type')] = deliveryLocationType.name || deliveryLocationType.reference || '';
	}

	if (appointmentData && appointmentData.startDate) {
		const startDate = new Date(appointmentData.startDate);
		fields[t('Date')] = startDate.toLocaleDateString();

		if (appointmentData.endDate) {
			const endDate = new Date(appointmentData.endDate);
			const startTime = startDate.toLocaleTimeString().substring(0, 5);
			const endTime = endDate.toLocaleTimeString().substring(0, 5);
			fields[t('Hours')] = `${startTime} - ${endTime}`;
		}
	}

	if (appointmentData && appointmentData._statements && appointmentData._statements.length > 0 && appointmentData.startDate) {
		fields[t('Date_Last_Statement')] = new Date(appointmentData.startDate).toLocaleDateString();
	}

	if (deliveryLocationId) {
		try {
			const frequency = await getVisitFrequency(deliveryLocationId);
			if (frequency) {
				fields[t('CurrentVisitRate')] = frequency;
			}

			const kpis = appointmentData ? await getDeliveryLocationKPIs(deliveryLocationId, appointmentData) : [];
			detailMarkerData.value.kpis = Array.isArray(kpis) ? kpis : [];
		} catch (e) {
			detailMarkerData.value.kpis = [];
		}
	} else {
		detailMarkerData.value.kpis = [];
	}

	if (detailMarkerData.value.address) {
		fields[t('Address')] = detailMarkerData.value.address;
	}

	detailMarkerData.value.fields = fields;

	highlightAppointment(detailMarkerData.value);
}

function highlightAppointment(detailMarkerData) {
	todayAppointments.value.forEach((ta) => {
		ta.isHighlighted = false;
	});

	if (!detailMarkerData) return;

	const clickedMapLocation = todayAppointments.value.find((ta) => ta.deliveryLocationId === detailMarkerData.id);
	if (clickedMapLocation) {
		clickedMapLocation.isHighlighted = true;
	}
}

function getRouteSegmentStart() {
	if (!nextAppointmentAfterCompleted.value) return 0;

	const lastCompletedIndex = todayAppointments.value.findIndex((apt) => apt.status === enumAppointment.status.VALIDATED && apt.deliveryLocationId !== user.deliveryLocationHomeId);

	if (lastCompletedIndex === -1) return 0;

	const validAppointments = todayAppointments.value.filter((apt) => apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED);

	const completedCount = validAppointments.filter((apt) => apt.status === enumAppointment.status.VALIDATED).length;

	return ((completedCount - 0.1) / validAppointments.length) * 100;
}

function getRouteSegmentEnd() {
	if (!nextAppointmentAfterCompleted.value) return 0;

	const validAppointments = todayAppointments.value.filter((apt) => apt.deliveryLocationId !== user.deliveryLocationHomeId && apt.status !== enumAppointment.status.CANCELLED);

	const completedCount = validAppointments.filter((apt) => apt.status === enumAppointment.status.VALIDATED).length;

	return ((completedCount + 0.5) / validAppointments.length) * 100;
}

// #endregion

// #region ORDERS

async function getOrders() {
	const assortOrderOption = iframeDataBridge.getEcosVM().options.find((opt) => opt.name === 'ProductAssortOrder');

	if (!assortOrderOption || (assortOrderOption && assortOrderOption.value !== 'true')) {
		hasAssortOrder.value = false;
		return;
	}

	try {
		const today = new Date();
		const { orders: fetchedOrders } = await getDirectOrders({ startDate: today, endDate: today });
		const orders = filterOrders(fetchedOrders, { startDate: today, endDate: today, orderTypes: [ORDER_TYPE.REGULAR] });

		const groups = orders.map((order) => ({
			id: order.id,
			totalDiscountValue: Math.round(order.lines.reduce((sum, line) => sum + (line.valueDiscounted || 0), 0) * 100) / 100,
		}));

		const totalDiscountValue = groups.reduce((sum, group) => sum + group.totalDiscountValue, 0);
		salesAmount.value = `${totalDiscountValue} ${iframeDataBridge.getExternis().sales.salesHelper.getCurrency()}`;
	} catch (error) {
		console.error('Error in getOrders:', error);
	}
}

// #endregion

// #region MENU

function handleMenuClick(appointment, action) {
	if (action === 'map') showOnMap(appointment);
	if (action === 'cancel') cancelAppointment(appointment);
	openMenuId.value = null;
}

// #endregion

// #region Filters & Toolbox

function onFiltersChanged(newFilteredLocations) {
	mapLocations.value = newFilteredLocations || [];
}

function onToolboxAction(actionData) {
	switch (actionData.toolId) {
		case 'allDeliveryLocations':
			updateMapLocations();
			break;

		case 'clearRoutes':
			resetDrawnRoute();
			updateMapLocations();
			break;

		default:
			break;
	}
}

// #endregion

function handleActionFocus() {
	if (!isFullscreen.value) {
		toggleFullscreen();
	}
}

function toggleFullscreen() {
	isFullscreen.value = !isFullscreen.value;
	showMap.value = isFullscreen.value;

	if (!isFullscreen.value && mapRef.value?.hideDetailMarker) {
		mapRef.value.hideDetailMarker();
	}

	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myday',
		fullScreened: isFullscreen.value,
		callback: async () => {
			resizeMap();
			resizeProgressBar();
			if (isFullscreen.value) {
				await nextTick();
				if (mapRef.value && mapRef.value.fitAll) {
					mapRef.value.fitAll();
				}
			}
		},
	});
}

function exitFullscreen() {
	isFullscreen.value = false;
	showMap.value = false;
	eventBus.emit('widget-fullscreened', {
		widget: 'widget-myday',
		fullScreened: isFullscreen.value,
	});
}

function resizeMap() {
	nextTick(() => {
		if (mapRef.value && mapRef.value.resize) {
			mapRef.value.resize();
			mapRef.value.resizeDetailMarkerContent();
		}
	});
}

function resizeProgressBar() {
	setTimeout(() => {
		if (!timelineProgressBar.value || !isTouchMode.value) return;
		const timeLineItems = timelineContainerRef.value ? timelineContainerRef.value.querySelectorAll('.v-timeline-divider, .route-info-item').length : 0;
		const appointmentRougeInfosHeight = root.value ? root.value.querySelectorAll('.appointment-rouge-info').offsetHeight : 0;
		timelineProgressBar.value.style.height = isTouchMode.value ? timeLineItems * (46 + 24) - appointmentRougeInfosHeight + 'px' : '100%';
	}, 300);
}

defineExpose({
	toggleFullscreen,
	isFullscreen,
});
</script>

<style lang="scss" scoped>
.no-appointments-message {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	color: #757575;
	height: 100%;

	.no-appointments-icon {
		font-size: 48px;
		width: 48px;
		height: 48px;
		margin-bottom: 16px;
		opacity: 0.6;
	}

	p {
		margin: 0;
		font-size: 16px;
		font-weight: 500;
	}
}
</style>
